/* eslint-disable react-hooks/exhaustive-deps */
/**
 * LikiditeDerinlikTarama.jsx
 * ───────────────────────────
 * Likidite Derinliği / Slippage Analizi
 * Renk paleti & bileşen yapısı: YatirimOneriMotoru.jsx ile aynı
 */

import { useState, useEffect, useRef } from "react";
import { supabase } from "../supabaseClient";
import axios from "axios";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";
import { API_URL } from "../config";
import { useNavigate } from "react-router-dom";
import { getLookupPeriyotlar } from "./apiCache";

// ─── Renk Paleti (YatirimOneriMotoru ile identik) ────────────────────────────
const C = {
    bg:      "#0f172a",
    panel:   "#1e293b",
    border:  "#334155",
    text:    "#e2e8f0",
    muted:   "#94a3b8",
    accent:  "#3b82f6",
    bullish: "#16a34a",
    bearish: "#dc2626",
    warning: "#f59e0b",
    gold:    "#eab308",
    purple:  "#7c3aed",
    teal:    "#0d9488",
};

// ─── Yardımcılar ─────────────────────────────────────────────────────────────
const fmt = (v, digits = 2) =>
    v == null ? "—" : Number(v).toLocaleString("tr-TR", { minimumFractionDigits: digits, maximumFractionDigits: digits });

const getToken = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    return session?.access_token;
};

function formatSure(sn) {
    if (sn == null || sn < 0) return "—";
    const h = Math.floor(sn / 3600);
    const m = Math.floor((sn % 3600) / 60);
    const s = Math.floor(sn % 60);
    if (h > 0) return `${String(h).padStart(2,"0")}:${String(m).padStart(2,"0")}:${String(s).padStart(2,"0")}`;
    return `${String(m).padStart(2,"0")}:${String(s).padStart(2,"0")}`;
}

// ─── Renk Yardımcıları ───────────────────────────────────────────────────────
const likiditeSkorRenk = (s) =>
    s >= 80 ? C.bullish : s >= 60 ? C.accent : s >= 40 ? C.warning : C.bearish;

const trendRenk = (t = "") =>
    t.includes("Güçlü Artıyor") ? C.bullish :
    t.includes("Artıyor")       ? C.accent  :
    t.includes("Yatay")         ? C.muted   :
    t.includes("Güçlü Azalıyor")? C.bearish : C.warning;

const slipRenk = (s) =>
    s < 0.5  ? C.bullish :
    s < 1.0  ? C.accent  :
    s < 2.0  ? C.warning : C.bearish;

// ─── Bilgi İkonu ─────────────────────────────────────────────────────────────
const BILGI_ICERIK = {
    likidite_skoru: {
        baslik: "Likidite Skoru (0-100)",
        aciklama: "Hacim seviyesi (%55), hacim trendi (%25) ve slippage skoru (%20) ağırlıklı bileşimi. 80+ yüksek likidite, 60-80 orta, 40-60 düşük, 40 altı çok düşük likiditeyi gösterir.",
    },
    durum: {
        baslik: "Likidite Durumu",
        aciklama: "Bileşik likidite skoruna göre atanan etiket: 🟢 Yüksek (80+), 🔵 Orta (60-80), 🟡 Düşük (40-60), 🔴 Çok Düşük (40 altı). YOM entegrasyonunda bu durum, pozisyon küçültme kararını etkiler.",
    },
    ort_hacim: {
        baslik: "Ortalama Günlük Hacim",
        aciklama: "Son 20 işlem gününün ortalama işlem hacmi (TL veya USDT). Bu değer, pozisyon büyüklüğünün piyasada karşılık bulup bulamayacağının temel ölçütüdür. Hacim düşükse büyük pozisyonlar fiyatı olumsuz etkiler.",
    },
    hacim_skor: {
        baslik: "Hacim Seviyesi Skoru",
        aciklama: "Ortalama günlük hacmin logaritmik ölçekte puanlanması. BIST'te 10M TL = orta (50 puan), 100M TL = iyi (80 puan), 1B TL = mükemmel (100 puan). Kripto'da eşdeğer USDT değerleri kullanılır.",
    },
    trend: {
        baslik: "Hacim Trendi",
        aciklama: "Son 5 günün ortalama hacmi / önceki 15 günün ortalama hacmi oranı. 1.30 üzeri Güçlü Artıyor, 1.10-1.30 Artıyor, 0.90-1.10 Yatay, 0.70-0.90 Azalıyor, 0.70 altı Güçlü Azalıyor. Artış trendi likidite kalitesini iyileştirir.",
    },
    trend_skor: {
        baslik: "Hacim Trendi Skoru",
        aciklama: "Hacim trendinin puanlanmış hali: Güçlü Artıyor=100, Artıyor=80, Yatay=60, Azalıyor=35, Güçlü Azalıyor=10. Azalan hacim, piyasanın derinlik kaybettiğine işaret eder.",
    },
    slip_pct: {
        baslik: "Tahmini Slippage %",
        aciklama: "Referans pozisyonunuzun ortalama günlük hacme oranı × spread çarpanı (BIST %0.10, Kripto %0.15). Pozisyonunuzun piyasayı ne kadar hareket ettirebileceğini modelleyerek tahmin eder. %0.5 altı mükemmel, %1 altı kabul edilebilir, %2 üzeri yüksek risk.",
    },
    slip_skor: {
        baslik: "Slippage Skoru",
        aciklama: "Tahmini slippage yüzdesinin ters puanlanması — düşük slippage yüksek skor demektir: %0.1 altı=100, %0.5 altı=85, %1 altı=65, %2 altı=40, %5 altı=20, %5 üzeri=5. Skor 40 altına düştüğünde pozisyon YOM tarafından otomatik küçültülür.",
    },
    poz_hacim: {
        baslik: "Pozisyon / Günlük Hacim %",
        aciklama: "Referans pozisyonunuzun günlük ortalama hacme oranı. Belirlediğiniz eşiği (varsayılan %3) aşıyorsa pozisyon piyasada tam olarak karşılanamaz ve fiyatı olumsuz etkileyebilir. Bu durumda ⚠️ uyarısı görünür.",
    },
    referans_pozisyon: {
        baslik: "Referans Pozisyon",
        aciklama: "Slippage ve hacim yeterliliği hesaplamalarında kullanılacak pozisyon büyüklüğü (TL veya USDT). Risk Filtresi'nizde tanımlı portföy büyüklüğünüzün bir kısmını girin — örn. 100.000 TL portföy için %5 pozisyon = 5.000 TL.",
    },
    hacim_esigi: {
        baslik: "Hacim Eşiği (Poz/Hacim Max %)",
        aciklama: "Pozisyon büyüklüğünün günlük ortalama hacme oranının kabul edilebilir üst sınırı. Bu oranı aşan semboller ⚠️ ile işaretlenir; YOM entegrasyonunda pozisyon otomatik olarak bu eşiğe uyacak şekilde küçültülür. Bireysel yatırımcılar için %3-5 önerilir.",
    },
    max_slippage: {
        baslik: "Maksimum Kabul Edilebilir Slippage %",
        aciklama: "İşlem gerçekleştirirken göze alabileceğiniz maksimum fiyat kayması yüzdesi. Bu eşiği aşan tahminî slippage'e sahip semboller ⚠️ ile işaretlenir ve YOM entegrasyonunda pozisyon küçültmeye konu olur. Gün içi işlemlerde %0.5, orta vade için %1 önerilir.",
    },
};

function BilgiIkonu({ karti }) {
    const [acik, setAcik] = useState(false);
    const bilgi = BILGI_ICERIK[karti];
    if (!bilgi) return null;
    return (
        <span
            onMouseEnter={e => { e.stopPropagation(); setAcik(true); }}
            onMouseLeave={e => { e.stopPropagation(); setAcik(false); }}
            onClick={e => e.stopPropagation()}
            style={{
                position: "relative", marginLeft: 4, fontSize: 11, color: C.muted, cursor: "help",
                borderRadius: "50%", border: `1px solid ${C.border}`,
                padding: "0 4px", lineHeight: "16px", display: "inline-block",
                userSelect: "none",
            }}>
            ℹ
            {acik && (
                <div style={{
                    position: "absolute", top: "calc(100% + 8px)", left: "50%", transform: "translateX(-50%)",
                    background: C.panel, border: `1px solid ${C.border}`, borderRadius: 8,
                    padding: "10px 12px", width: 260, boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
                    zIndex: 1000, textAlign: "left", whiteSpace: "normal", fontWeight: 400,
                }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 4 }}>{bilgi.baslik}</div>
                    <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.6 }}>{bilgi.aciklama}</div>
                </div>
            )}
        </span>
    );
}

// ─── İlerleme Paneli ─────────────────────────────────────────────────────────
function LikiditeIlerlemePaneli({ ilerleme, mesaj, bulunanSayisi, tamamlanan, toplamSayisi, gecenSure, tahminiSure }) {
    return (
        <div style={{
            marginBottom: 16, background: C.panel,
            border: `2px solid ${C.teal}44`, borderRadius: 12, padding: 20,
            boxShadow: `0 0 20px ${C.teal}18`,
        }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                <span style={{ fontSize: 20 }}>💧</span>
                <span style={{ fontSize: 15, fontWeight: 700, color: C.text }}>
                    Likidite Derinlik Analizi çalışıyor...
                </span>
                <span style={{ marginLeft: "auto", fontSize: 22, fontWeight: 800, color: C.teal }}>
                    %{ilerleme.toFixed(0)}
                </span>
            </div>

            <div style={{ height: 10, background: C.bg, borderRadius: 6, overflow: "hidden", marginBottom: 12, border: `1px solid ${C.border}` }}>
                <div style={{
                    height: "100%", width: `${ilerleme}%`,
                    background: `linear-gradient(90deg, ${C.teal}, ${C.accent})`,
                    borderRadius: 6, transition: "width 0.5s ease",
                }} />
            </div>

            <div style={{ fontSize: 13, color: C.text, marginBottom: 14, minHeight: 18 }}>
                {mesaj || "Analiz ediliyor..."}
            </div>

            <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
                <div>
                    <div style={{ fontSize: 10, color: C.muted, marginBottom: 2 }}>TAMAMLANAN</div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: C.accent }}>
                        {tamamlanan.toLocaleString("tr-TR")} / {toplamSayisi.toLocaleString("tr-TR")}
                    </div>
                </div>
                <div>
                    <div style={{ fontSize: 10, color: C.muted, marginBottom: 2 }}>BULUNAN</div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: C.teal }}>{bulunanSayisi}</div>
                </div>
                <div>
                    <div style={{ fontSize: 10, color: C.muted, marginBottom: 2 }}>GEÇEN SÜRE</div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: C.teal }}>{formatSure(gecenSure)}</div>
                </div>
                <div>
                    <div style={{ fontSize: 10, color: C.muted, marginBottom: 2 }}>TAHMİNİ KALAN</div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: C.gold }}>
                        {tahminiSure != null ? formatSure(tahminiSure) : "Hesaplanıyor..."}
                    </div>
                </div>
            </div>
        </div>
    );
}

// ─── Mini Skor Çubuğu ────────────────────────────────────────────────────────
function SkorCubugu({ val, renk }) {
    return (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ flex: 1, height: 6, background: C.bg, borderRadius: 99, overflow: "hidden", border: `1px solid ${C.border}` }}>
                <div style={{
                    width: `${Math.min(100, Math.max(0, val || 0))}%`,
                    height: "100%", background: renk, borderRadius: 99,
                    transition: "width 0.4s"
                }} />
            </div>
            <span style={{ fontSize: 11, fontWeight: 700, color: renk, minWidth: 26, textAlign: "right" }}>
                {Math.round(val || 0)}
            </span>
        </div>
    );
}

// ─── Likidite Rozeti ─────────────────────────────────────────────────────────
function LikiditeBadge({ etiket, skor }) {
    const c  = likiditeSkorRenk(skor);
    return (
        <span style={{
            padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700,
            background: c + "22", color: c, border: `1px solid ${c}55`, whiteSpace: "nowrap"
        }}>
            {etiket || "—"}
        </span>
    );
}

// ─── İndir Menüsü ────────────────────────────────────────────────────────────
function IndirMenusu({ veri, setIslemMenuAcik }) {
    const [acik, setAcik] = useState(false);
    const dosyaAdi = `likidite_derinlik_${new Date().toLocaleDateString("tr-TR").replace(/\./g, "-")}`;

    const indir = async (fmtTip) => {
        setAcik(false); setIslemMenuAcik(false);
        if (!veri?.length) return;
        if (fmtTip === "xlsx") {
            const ws = XLSX.utils.json_to_sheet(veri);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Likidite Analizi");
            XLSX.writeFile(wb, `${dosyaAdi}.xlsx`);
        } else if (fmtTip === "csv") {
            const h = Object.keys(veri[0] || {});
            const rows = veri.map(r => h.map(k => {
                const v = String(r[k] ?? "");
                return v.includes(",") || v.includes('"') ? `"${v.replace(/"/g, '""')}"` : v;
            }).join(","));
            const blob = new Blob(["\uFEFF" + [h.join(","), ...rows].join("\n")], { type: "text/csv;charset=utf-8;" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a"); a.href = url; a.download = `${dosyaAdi}.csv`; a.click(); URL.revokeObjectURL(url);
        } else if (fmtTip === "pdf") {
            try {
                const { default: jsPDF } = await import("jspdf");
                const { default: autoTable } = await import("jspdf-autotable");
                const h = Object.keys(veri[0] || {});
                const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
                doc.setFontSize(14); doc.text("Likidite Derinlik Analizi", 20, 30);
                doc.setFontSize(9); doc.setTextColor(100);
                doc.text(`${veri.length} kayıt · ${new Date().toLocaleDateString("tr-TR")}`, 20, 48);
                autoTable(doc, {
                    head: [h], body: veri.map(r => h.map(k => String(r[k] ?? ""))), startY: 58,
                    styles: { fontSize: 7, cellPadding: 3 },
                    headStyles: { fillColor: [13, 148, 136], textColor: 255, fontStyle: "bold" },
                    alternateRowStyles: { fillColor: [245, 247, 250] },
                });
                doc.save(`${dosyaAdi}.pdf`);
            } catch { toast.error("PDF oluşturulamadı"); }
        } else if (fmtTip === "svg") {
            const h = Object.keys(veri[0] || {});
            const cw = Math.max(60, Math.floor(740 / h.length)), rh = 18, hh = 24, tw = cw * h.length;
            const pw = Math.max(tw + 40, 800), ph = 80 + hh + veri.length * rh;
            const esc = s => String(s).slice(0, 18).replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;");
            const rows = veri.map((r, i) => `<g transform="translate(20,${60 + hh + i * rh})"><rect width="${tw}" height="${rh}" fill="${i%2===0?"#1e293b":"#0f172a"}" stroke="#334155" stroke-width="0.5"/>${h.map((k,j)=>`<text x="${j*cw+4}" y="13" font-size="9" fill="#e2e8f0" font-family="Arial">${esc(r[k])}</text>`).join("")}</g>`).join("");
            const heads = h.map((k,j)=>`<text x="${20+j*cw+4}" y="48" font-size="9" font-weight="bold" fill="white" font-family="Arial">${esc(k)}</text>`).join("");
            const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${pw}" height="${ph}"><rect width="${pw}" height="${ph}" fill="#0f172a"/><text x="20" y="22" font-size="14" font-weight="bold" fill="#e2e8f0" font-family="Arial">Likidite Derinlik Analizi</text><text x="20" y="38" font-size="10" fill="#94a3b8" font-family="Arial">${veri.length} kayıt · ${new Date().toLocaleDateString("tr-TR")}</text><rect x="20" y="52" width="${tw}" height="${hh}" fill="#0d9488"/>${heads}${rows}</svg>`;
            const blob = new Blob([svg], { type: "image/svg+xml" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a"); a.href = url; a.download = `${dosyaAdi}.svg`; a.click(); URL.revokeObjectURL(url);
        }
    };

    return (
        <div style={{ position: "relative" }} onMouseEnter={() => setAcik(true)} onMouseLeave={() => setAcik(false)}>
            <button style={{ display: "flex", width: "100%", textAlign: "left", justifyContent: "space-between",
                padding: "10px 16px", border: "none", background: "none", cursor: "pointer",
                color: C.text, fontSize: 13, borderBottom: `1px solid ${C.border}` }}>
                📥 İndir <span style={{ color: C.muted }}>▶</span>
            </button>
            {acik && (
                <div style={{ position: "absolute", left: "100%", top: 0, background: C.panel,
                    border: `1px solid ${C.border}`, borderRadius: 8,
                    boxShadow: "0 8px 24px rgba(0,0,0,0.4)", minWidth: 160, overflow: "hidden", zIndex: 101 }}>
                    {[{ l: "Excel (.xlsx)", f: "xlsx" }, { l: "CSV (.csv)", f: "csv" }, { l: "PDF (.pdf)", f: "pdf" }, { l: "SVG (.svg)", f: "svg" }].map(s => (
                        <button key={s.f} onClick={() => indir(s.f)}
                            style={{ display: "block", width: "100%", textAlign: "left", padding: "9px 14px",
                                border: "none", background: "none", cursor: "pointer", color: C.text, fontSize: 13,
                                borderBottom: `1px solid ${C.border}` }}>{s.l}</button>
                    ))}
                </div>
            )}
        </div>
    );
}

// ─── Ana Bileşen ─────────────────────────────────────────────────────────────
export default function LikiditeDerinlikTarama() {
    const navigate = useNavigate();

    // Parametreler
    const [piyasa,      setPiyasa]      = useState("BIST");
    const [paraBirimi,  setParaBirimi]  = useState("TL");
    const [pozisyonTL,  setPozisyonTL]  = useState(10000);
    const [hacimEsigi,  setHacimEsigi]  = useState(3);
    const [maxSlippage, setMaxSlippage] = useState(1.0);

    // Tarama durumu
    const [calisiyor,    setCalisiyor]    = useState(false);
    const [ilerleme,     setIlerleme]     = useState(0);
    const [durumMesaj,   setDurumMesaj]   = useState(null);
    const [bulunanSayisi,setBulunanSayisi]= useState(0);
    const [tamamlanan,   setTamamlanan]   = useState(0);
    const [toplamSayisi, setToplamSayisi] = useState(0);
    const [gecenSure,    setGecenSure]    = useState(0);
    const [tahminiSure,  setTahminiSure]  = useState(null);
    const [sonuc,        setSonuc]        = useState(null);

    // Tablo & seçim
    const [siralama,      setSiralama]      = useState({ kol: "likidite_skoru", yon: "desc" });
    const [minSkor,       setMinSkor]       = useState(0);
    const [trendFiltre,   setTrendFiltre]   = useState("Tümü");
    const [aramaMetni,    setAramaMetni]    = useState("");
    const [secilenler,    setSecilenler]    = useState(new Set());
    const [islemMenuAcik, setIslemMenuAcik] = useState(false);

    // Alarm popup
    const [alarmPopup,       setAlarmPopup]       = useState(false);
    const [alarmHisseler,    setAlarmHisseler]     = useState([]);
    const [hedefFiyatlar,    setHedefFiyatlar]     = useState({});
    const [alarmMail,        setAlarmMail]         = useState(false);
    const [alarmBildirim,    setAlarmBildirim]     = useState(true);
    const [alarmKaydediliyor,setAlarmKaydediliyor] = useState(false);

    // Çoklu Tarama popup
    const [cokluPopup,     setCokluPopup]     = useState(false);
    const [cokluSecModul,  setCokluSecModul]  = useState(null);
    const [cokluFiltre,    setCokluFiltre]    = useState(null);
    const [cokluOnayPopup, setCokluOnayPopup] = useState(false);
    const [cokluPeriyotlar,setCokluPeriyotlar]= useState([]);

    // Puanlama popup
    const [puanlamaPopupAdim, setPuanlamaPopupAdim] = useState(null);
    const [puanlamaVade,      setPuanlamaVade]      = useState(null);
    const [puanlamaYon,       setPuanlamaYon]       = useState(new Set(["Long", "Short"]));
    const [puanlamaMinPuan,   setPuanlamaMinPuan]   = useState(0);

    // Geçmiş Simülasyon popup
    const [gecmisPopupAdim,  setGecmisPopupAdim]  = useState(null);
    const [gecmisSimTarihi,  setGecmisSimTarihi]  = useState(() => {
        const d = new Date(); d.setFullYear(d.getFullYear() - 1);
        return d.toISOString().split("T")[0];
    });

    const intervalRef = useRef(null);
    const surRef      = useRef(null);
    const baslamaRef  = useRef(null);
    const menuRef     = useRef(null);

    const PB_SECENEKLER = { BIST: ["TL", "USD"], Kripto: ["USDT", "USDT Futures"] };

    useEffect(() => {
        setParaBirimi(PB_SECENEKLER[piyasa][0]);
        setSonuc(null);
        setSecilenler(new Set());
    }, [piyasa]);

    useEffect(() => {
        const handler = e => {
            if (menuRef.current && !menuRef.current.contains(e.target)) setIslemMenuAcik(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    useEffect(() => () => {
        clearInterval(intervalRef.current);
        clearInterval(surRef.current);
    }, []);

    // ── Durum Sorgula ────────────────────────────────────────────────────────
    const durumSorgula = async () => {
        try {
            const token = await getToken();
            const res = await axios.get(`${API_URL}/scan/likidite-derinlik/durum`,
                { headers: { Authorization: `Bearer ${token}` } });
            setIlerleme(res.data.ilerleme || 0);
            setTahminiSure(res.data.tahmini_sure);

            const mesaj = res.data.mesaj || "";
            if (mesaj.includes("|")) {
                const p = mesaj.split("|");
                const b = parseInt(p[0], 10);
                if (!isNaN(b)) setBulunanSayisi(b);
                if (p[1] && p[1].includes("/")) {
                    const [t, top] = p[1].split("/");
                    if (!isNaN(parseInt(t, 10)))   setTamamlanan(parseInt(t, 10));
                    if (!isNaN(parseInt(top, 10))) setToplamSayisi(parseInt(top, 10));
                }
                setDurumMesaj(p[2] ? `Analiz edilen: ${p[2]}` : p[1] || mesaj);
            } else {
                setDurumMesaj(mesaj);
            }

            if (!res.data.calisiyor) {
                clearInterval(intervalRef.current);
                clearInterval(surRef.current);
                setCalisiyor(false);
                if (res.data.sonuc) {
                    setSonuc(res.data.sonuc);
                    setBulunanSayisi(res.data.sonuc.toplam || 0);
                    setSecilenler(new Set());
                    toast.success(`✅ Tamamlandı! ${res.data.sonuc.toplam} sembol analiz edildi`);
                } else if (res.data.hata) {
                    toast.error("Hata: " + res.data.hata);
                }
            }
        } catch { }
    };

    // ── Tarama Başlat ────────────────────────────────────────────────────────
    const taramaBaslat = async () => {
        setSonuc(null);
        setBulunanSayisi(0);
        setTamamlanan(0);
        setToplamSayisi(0);
        setCalisiyor(true);
        setIlerleme(0);
        setDurumMesaj(null);
        setGecenSure(0);
        setSecilenler(new Set());
        baslamaRef.current = Date.now();
        clearInterval(surRef.current);
        surRef.current = setInterval(
            () => setGecenSure(Math.floor((Date.now() - baslamaRef.current) / 1000)), 1000
        );
        try {
            const token = await getToken();
            await axios.post(`${API_URL}/scan/likidite-derinlik/baslat`, {
                mod:          piyasa,
                para_birimi:  paraBirimi,
                pozisyon_tl:  pozisyonTL,
                hacim_esigi:  hacimEsigi,
                max_slippage: maxSlippage,
            }, { headers: { Authorization: `Bearer ${token}` } });
            toast.success("Likidite analizi başlatıldı");
            intervalRef.current = setInterval(durumSorgula, 2500);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Hata oluştu");
            setCalisiyor(false);
            clearInterval(surRef.current);
        }
    };

    // ── Seçim ────────────────────────────────────────────────────────────────
    const siralamaToggle = (kol) =>
        setSiralama(s => ({ kol, yon: s.kol === kol && s.yon === "desc" ? "asc" : "desc" }));
    const siralamaOk = (kol) =>
        siralama.kol === kol ? (siralama.yon === "desc" ? " ↓" : " ↑") : "";

    const TREND_SECENEKLER = ["Tümü", "Güçlü Artıyor", "Artıyor", "Yatay", "Azalıyor", "Güçlü Azalıyor"];

    const filtreli = (sonuc?.sonuclar || [])
        .filter(r => r.likidite_skoru >= minSkor)
        .filter(r => trendFiltre === "Tümü" || r.trend_etiket === trendFiltre)
        .filter(r => !aramaMetni || r.symbol.toLowerCase().includes(aramaMetni.toLowerCase()))
        .sort((a, b) => {
            const av = a[siralama.kol] ?? 0, bv = b[siralama.kol] ?? 0;
            return siralama.yon === "desc" ? bv - av : av - bv;
        });

    const tumSecili = filtreli.length > 0 && secilenler.size === filtreli.length;
    const toggleTumSec = () => {
        if (tumSecili) setSecilenler(new Set());
        else setSecilenler(new Set(filtreli.map(r => r.symbol)));
    };
    const toggleSec = (symbol) => setSecilenler(prev => {
        const n = new Set(prev);
        n.has(symbol) ? n.delete(symbol) : n.add(symbol);
        return n;
    });

    // ── Çoklu Tarama ─────────────────────────────────────────────────────────
    const cokluAc = () => {
        if (secilenler.size === 0) { toast.error("Önce sembol seçin"); return; }
        setCokluSecModul(null); setCokluFiltre(null); setCokluPeriyotlar([]);
        setCokluPopup(true); setIslemMenuAcik(false);
    };

    // ── Geçmiş Simülasyon ────────────────────────────────────────────────────
    const gecmisSimAc = () => {
        if (secilenler.size === 0) { toast.error("Önce sembol seçin"); return; }
        setIslemMenuAcik(false);
        setGecmisPopupAdim("kriter");
    };
    const gecmisSimOnay = () => {
        const hisseler = filtreli.filter(r => secilenler.has(r.symbol)).map(r => r.symbol);
        const simParams = {
            kaynak:        "likidite_derinlik",
            kaynak_label:  "Likidite Derinlik Analizi",
            mod:           piyasa === "Kripto" ? "KRIPTO" : "BIST",
            para_birimi:   paraBirimi,
            sim_tarihi:    gecmisSimTarihi,
            ref_fiyat:     "Bugünkü",
            hisse_listesi: hisseler,
            oto_baslat:    true,
        };
        setGecmisPopupAdim(null);
        navigate("/gecmis-simulasyon", { state: simParams });
    };

    // ── Puanlama ─────────────────────────────────────────────────────────────
    const puanlamaAc = () => {
        if (secilenler.size === 0) { toast.error("Önce sembol seçin"); return; }
        const bistOpts = ["Gün İçi", "Kısa Vade (1-10 gün)", "Orta Vade (11-60 gün)", "Uzun Vade (61+ gün)"];
        setPuanlamaVade(piyasa === "Kripto" ? "Kaldıraçlı İşlem" : bistOpts[0]);
        setPuanlamaYon(new Set(["Long", "Short"]));
        setPuanlamaMinPuan(0);
        setPuanlamaPopupAdim("kriter");
        setIslemMenuAcik(false);
    };
    const togglePuanlamaYon = (y) => setPuanlamaYon(prev => {
        const n = new Set(prev);
        if (n.has(y)) { if (n.size === 1) return prev; n.delete(y); } else n.add(y);
        return n;
    });

    // ── Alarm ────────────────────────────────────────────────────────────────
    const alarmAc = () => {
        if (secilenler.size === 0) { toast.error("Önce sembol seçin"); return; }
        const seciliH = filtreli
            .filter(r => secilenler.has(r.symbol))
            .map(r => ({ symbol: r.symbol, fiyat: null })); // likidite modülünde anlık fiyat yok
        setAlarmHisseler(seciliH);
        setHedefFiyatlar({});
        setAlarmMail(false);
        setAlarmBildirim(true);
        setAlarmPopup(true);
        setIslemMenuAcik(false);
    };

    const alarmKaydet = async () => {
        const eksik = alarmHisseler.find(h => !hedefFiyatlar[h.symbol] || isNaN(parseFloat(hedefFiyatlar[h.symbol])));
        if (eksik) { toast.error(`${eksik.symbol} için hedef fiyat girin`); return; }
        setAlarmKaydediliyor(true);
        try {
            const { data: { session } } = await supabase.auth.getSession();
            for (const h of alarmHisseler) {
                const hedef = parseFloat(hedefFiyatlar[h.symbol]);
                const { error } = await supabase.from("alerts").insert({
                    piyasa, sembol: h.symbol, para_birimi: paraBirimi,
                    fiyat: null, hedef_fiyat: hedef, potansiyel: null,
                    bildirim_mail: alarmMail, bildirim_popup: alarmBildirim,
                    user_id: session.user.id,
                });
                if (error) throw error;
            }
            toast.success(`${alarmHisseler.length} alarm eklendi!`);
            setAlarmPopup(false);
            setAlarmHisseler([]);
            setSecilenler(new Set());
        } catch (e) { toast.error("Alarm kaydedilemedi: " + e.message); }
        finally { setAlarmKaydediliyor(false); }
    };

    // ── Export verisi ────────────────────────────────────────────────────────
    const exportVeri = filtreli.map(r => ({
        "Sembol":          r.symbol,
        "Likidite Skoru":  r.likidite_skoru,
        "Durum":           r.etiket?.replace(/[🟢🔵🟡🔴]/g, "").trim(),
        "Ort. Hacim":      r.ort_hacim_fmt,
        "Hacim Skoru":     r.hacim_skor,
        "Trend":           r.trend_etiket,
        "Trend Oranı":     r.trend_oran,
        "Trend Skoru":     r.trend_skor,
        "Slippage %":      r.slip_pct,
        "Slippage Skoru":  r.slip_skor,
        "Poz/Hacim %":     r.poz_hacim_pct,
        "Gün Sayısı":      r.gun_sayisi,
    }));

    // ── Tablo stilleri ───────────────────────────────────────────────────────
    const thStyle = () => ({
        padding: "8px 12px", textAlign: "left", fontSize: 11, fontWeight: 700,
        color: C.muted, background: C.bg, borderBottom: `1px solid ${C.border}`,
        cursor: "pointer", whiteSpace: "nowrap", userSelect: "none",
    });
    const tdStyle = {
        padding: "10px 12px", fontSize: 12, borderBottom: `1px solid ${C.border}`,
        verticalAlign: "middle", color: C.text,
    };

    // ─────────────────────────────────────────────────────────────────────────
    // RENDER
    // ─────────────────────────────────────────────────────────────────────────

    return (
        <div style={{ minHeight: "100vh", width: "100%", background: C.bg }}>
        <div style={{ padding: "24px", fontFamily: "Inter, system-ui, sans-serif", color: C.text }}>

            {/* ── Başlık ── */}
            <div style={{ marginBottom: 20 }}>
                <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: C.text }}>
                    💧 Likidite Derinliği / Slippage Analizi
                </h1>
                <p style={{ margin: "3px 0 0", fontSize: 13, color: C.muted }}>
                    Hacim yeterliliği · Hacim trendi · Tahmini slippage maliyeti · Bileşik likidite skoru (0-100)
                </p>
            </div>

            {/* ── Kontrol Paneli ── */}
            <div style={{
                background: C.panel, border: `1px solid ${C.border}`, borderRadius: 12,
                padding: "16px 20px", marginBottom: 18,
                display: "flex", gap: 14, alignItems: "flex-end", flexWrap: "wrap",
                opacity: calisiyor ? 0.5 : 1, pointerEvents: calisiyor ? "none" : "auto", transition: "opacity 0.3s",
            }}>
                {/* Piyasa */}
                <fieldset style={{ border: `1px solid ${C.border}`, borderRadius: 8, padding: "6px 12px 10px", margin: 0 }}>
                    <legend style={{ fontSize: 11, color: C.muted, fontWeight: 600, padding: "0 4px", letterSpacing: "0.05em" }}>PİYASA</legend>
                    <div style={{ display: "flex", gap: 6 }}>
                        {["BIST", "Kripto"].map(p => (
                            <button key={p} onClick={() => setPiyasa(p)} style={{
                                padding: "5px 14px", borderRadius: 6, fontWeight: 700, fontSize: 13, cursor: "pointer",
                                border: `2px solid ${piyasa === p ? C.teal : C.border}`,
                                background: piyasa === p ? C.teal + "22" : "transparent",
                                color: piyasa === p ? C.teal : C.muted,
                            }}>
                                {p === "BIST" ? "🇹🇷 BIST" : "₿ Kripto"}
                            </button>
                        ))}
                    </div>
                </fieldset>

                {/* Para Birimi */}
                <fieldset style={{ border: `1px solid ${C.border}`, borderRadius: 8, padding: "6px 12px 10px", margin: 0 }}>
                    <legend style={{ fontSize: 11, color: C.muted, fontWeight: 600, padding: "0 4px", letterSpacing: "0.05em" }}>PARA BİRİMİ</legend>
                    <div style={{ display: "flex", gap: 6 }}>
                        {(PB_SECENEKLER[piyasa] || []).map(v => (
                            <button key={v} onClick={() => setParaBirimi(v)} style={{
                                padding: "5px 10px", borderRadius: 6, fontWeight: 700, fontSize: 12, cursor: "pointer",
                                border: `2px solid ${paraBirimi === v ? "#5eead4" : C.border}`,
                                background: paraBirimi === v ? "#5eead422" : "transparent",
                                color: paraBirimi === v ? "#5eead4" : C.muted,
                            }}>{v}</button>
                        ))}
                    </div>
                </fieldset>

                {/* Referans Pozisyon */}
                <fieldset style={{ border: `1px solid ${C.border}`, borderRadius: 8, padding: "6px 12px 10px", margin: 0 }}>
                    <legend style={{ fontSize: 11, color: C.muted, fontWeight: 600, padding: "0 4px", letterSpacing: "0.05em", display: "flex", alignItems: "center", gap: 2 }}>
                        REFERANS POZİSYON ({piyasa === "BIST" ? "TL" : "USDT"})
                        <BilgiIkonu karti="referans_pozisyon" />
                    </legend>
                    <input
                        type="text"
                        inputMode="numeric"
                        value={pozisyonTL.toLocaleString("tr-TR")}
                        onChange={e => {
                            const raw = e.target.value.replace(/\./g, "").replace(/[^0-9]/g, "");
                            const num = parseInt(raw, 10);
                            if (!isNaN(num) && num >= 0) setPozisyonTL(num);
                            else if (raw === "") setPozisyonTL(0);
                        }}
                        style={{ padding: "6px 10px", borderRadius: 6, border: `1px solid ${C.border}`,
                            fontSize: 13, background: C.bg, color: C.text, outline: "none", width: 140 }}
                    />
                </fieldset>

                {/* Hacim Eşiği */}
                <fieldset style={{ border: `1px solid ${C.border}`, borderRadius: 8, padding: "6px 12px 10px", margin: 0, minWidth: 200 }}>
                    <legend style={{ fontSize: 11, color: C.muted, fontWeight: 600, padding: "0 4px", letterSpacing: "0.05em", display: "flex", alignItems: "center", gap: 2 }}>
                        HACİM EŞİĞİ (POZ/HACİM MAX %)
                        <BilgiIkonu karti="hacim_esigi" />
                    </legend>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <input type="range" min={0.5} max={20} step={0.5} value={hacimEsigi}
                            onChange={e => setHacimEsigi(Number(e.target.value))}
                            style={{ flex: 1, cursor: "pointer", accentColor: C.teal }} />
                        <span style={{ fontSize: 14, fontWeight: 800, color: C.teal, minWidth: 38 }}>
                            %{hacimEsigi}
                        </span>
                    </div>
                </fieldset>

                {/* Max Slippage */}
                <fieldset style={{ border: `1px solid ${C.border}`, borderRadius: 8, padding: "6px 12px 10px", margin: 0, minWidth: 200 }}>
                    <legend style={{ fontSize: 11, color: C.muted, fontWeight: 600, padding: "0 4px", letterSpacing: "0.05em", display: "flex", alignItems: "center", gap: 2 }}>
                        MAX SLIPPAGE %
                        <BilgiIkonu karti="max_slippage" />
                    </legend>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <input type="range" min={0.1} max={5} step={0.1} value={maxSlippage}
                            onChange={e => setMaxSlippage(Number(e.target.value))}
                            style={{ flex: 1, cursor: "pointer", accentColor: C.bearish }} />
                        <span style={{ fontSize: 14, fontWeight: 800, color: C.bearish, minWidth: 38 }}>
                            %{maxSlippage}
                        </span>
                    </div>
                </fieldset>

                {/* Başlat */}
                <button onClick={taramaBaslat} disabled={calisiyor}
                    style={{
                        padding: "10px 24px", borderRadius: 8, border: "none",
                        background: calisiyor ? C.border : `linear-gradient(135deg,#0f766e,${C.teal})`,
                        color: "white", fontWeight: 700, fontSize: 14,
                        cursor: calisiyor ? "not-allowed" : "pointer",
                    }}>
                    {calisiyor ? "⏳ Analiz Ediliyor..." : "💧 Analizi Başlat"}
                </button>
            </div>

            {/* ── Tarama Uyarısı ── */}
            {calisiyor && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10,
                    padding: "10px 16px", borderRadius: 8, background: "#022c22", border: "1px solid #065f46" }}>
                    <span style={{ fontSize: 16 }}>💧</span>
                    <span style={{ fontSize: 13, color: "#6ee7b7", fontWeight: 600 }}>
                        Tüm semboller analiz ediliyor — tamamlanana kadar sayfayı kapatmayın
                    </span>
                </div>
            )}

            {/* ── İlerleme Paneli ── */}
            {calisiyor && (
                <LikiditeIlerlemePaneli
                    ilerleme={ilerleme} mesaj={durumMesaj} bulunanSayisi={bulunanSayisi}
                    tamamlanan={tamamlanan} toplamSayisi={toplamSayisi}
                    gecenSure={gecenSure} tahminiSure={tahminiSure}
                />
            )}

            {/* ── Özet Kartlar ── */}
            {sonuc?.ozet && (
                <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
                    {[
                        { label: "🟢 Yüksek Likidite", val: sonuc.ozet.yuksek_likidite, renk: C.bullish },
                        { label: "🔵 Orta Likidite",   val: sonuc.ozet.orta_likidite,   renk: C.accent  },
                        { label: "🟡 Düşük Likidite",  val: sonuc.ozet.dusuk_likidite,  renk: C.warning },
                        { label: "🔴 Çok Düşük",       val: sonuc.ozet.cok_dusuk,       renk: C.bearish },
                        { label: "📊 Ort. Skor",        val: sonuc.ozet.ort_skor,        renk: C.text, birim: "/100" },
                        { label: "📋 Toplam",           val: sonuc.toplam,               renk: C.teal  },
                    ].map(({ label, val, renk, birim }) => (
                        <div key={label} style={{
                            background: C.panel, border: `1px solid ${renk}33`,
                            borderRadius: 10, padding: "10px 16px", flex: "1 1 120px", minWidth: 100
                        }}>
                            <div style={{ fontSize: 11, color: C.muted, marginBottom: 3 }}>{label}</div>
                            <div style={{ fontSize: 22, fontWeight: 800, color: renk }}>
                                {val}{birim || ""}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* ── Tablo Alanı ── */}
            {sonuc && (
                <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 12, overflow: "visible" }}>

                    {/* Tab Bar + İşlemler + Filtreler */}
                    <div style={{ display: "flex", alignItems: "center", padding: "0 12px", borderBottom: `1px solid ${C.border}`, flexWrap: "wrap" }}>

                        {/* İşlemler */}
                        <div style={{ position: "relative", padding: "8px 8px 8px 0" }} ref={menuRef}>
                            <button onClick={() => setIslemMenuAcik(v => !v)} style={{
                                background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8,
                                padding: "7px 14px", display: "flex", alignItems: "center", gap: 6,
                                color: C.text, cursor: "pointer", fontSize: 12, fontWeight: 600,
                            }}>İşlemler ▾ {secilenler.size > 0 && `(${secilenler.size})`}</button>
                            {islemMenuAcik && (
                                    <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, background: C.panel,
                                    border: `1px solid ${C.border}`, borderRadius: 10, boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
                                    zIndex: 100, minWidth: 280, overflow: "visible" }}>
                                    {[
                                        { label: "✅ Doğruluk Analizine Gönder",    aktif: false, onClick: () => {} },
                                        { label: "🔮 Geçmiş Simülasyonuna Gönder", aktif: false,  onClick: gecmisSimAc },
                                        { label: "🔗 Çoklu Taramaya Gönder",       aktif: true,  onClick: cokluAc },
                                        { label: "🏆 Puanlamaya Gönder",           aktif: true,  onClick: puanlamaAc },
                                        { label: "⏰ Alarm Ekle",                  aktif: true,  onClick: alarmAc },
                                        { label: "🔎 İzleme Listesine Ekle",       aktif: false, onClick: () => {} },
                                    ].map(item => (
                                        <button key={item.label}
                                            onClick={item.aktif ? item.onClick : undefined}
                                            disabled={!item.aktif}
                                            style={{ display: "block", width: "100%", textAlign: "left",
                                                padding: "10px 16px", border: "none", background: "none",
                                                cursor: item.aktif ? "pointer" : "default",
                                                color: item.aktif ? C.text : C.muted, fontSize: 13,
                                                borderBottom: `1px solid ${C.border}`,
                                                opacity: item.aktif ? 1 : 0.5,
                                            }}>
                                            {item.label}
                                            {!item.aktif && (
                                                <span style={{ marginLeft: 6, fontSize: 10, color: C.muted }}>(yakında)</span>
                                            )}
                                        </button>
                                    ))}
                                    <IndirMenusu veri={exportVeri} setIslemMenuAcik={setIslemMenuAcik} />
                                </div>
                            )}
                        </div>

                        {/* Başlık */}
                        <span style={{ fontSize: 13, fontWeight: 700, color: C.teal, padding: "10px 8px" }}>
                            💧 {filtreli.length} Sembol
                        </span>

                        {/* Arama */}
                        <input
                            placeholder="🔍 Sembol ara..."
                            value={aramaMetni} onChange={e => setAramaMetni(e.target.value)}
                            style={{ padding: "5px 10px", borderRadius: 7, border: `1px solid ${C.border}`,
                                background: C.bg, color: C.text, fontSize: 12, minWidth: 130, outline: "none", margin: "6px 8px" }}
                        />

                        {/* Min Skor */}
                        <div style={{ display: "flex", alignItems: "center", gap: 6, margin: "6px 0" }}>
                            <span style={{ fontSize: 11, color: C.muted, whiteSpace: "nowrap" }}>Min Skor:</span>
                            <input type="range" min={0} max={90} step={10} value={minSkor}
                                onChange={e => setMinSkor(Number(e.target.value))}
                                style={{ width: 80, cursor: "pointer", accentColor: C.teal }} />
                            <span style={{ fontSize: 11, fontWeight: 700, color: C.teal, minWidth: 20 }}>{minSkor}</span>
                        </div>

                        {/* Trend Filtresi */}
                        <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginLeft: "auto", padding: "4px 0" }}>
                            {TREND_SECENEKLER.map(t => (
                                <button key={t} onClick={() => setTrendFiltre(t)}
                                    style={{ padding: "3px 8px", borderRadius: 20, border: "none",
                                        cursor: "pointer", fontSize: 10, fontWeight: trendFiltre === t ? 700 : 400,
                                        background: trendFiltre === t ? C.teal + "33" : C.bg,
                                        color: trendFiltre === t ? C.teal : C.muted }}>
                                    {t}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Tablo */}
                    {filtreli.length === 0 ? (
                        <div style={{ padding: "48px 24px", textAlign: "center", color: C.muted, fontSize: 14 }}>
                            Filtre koşullarına uyan sembol bulunamadı.
                        </div>
                    ) : (
                        <div style={{ overflowX: "auto" }}>
                            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                <thead>
                                    <tr>
                                        <th style={{ ...thStyle(), width: 32, textAlign: "center" }}>
                                            <input type="checkbox" checked={tumSecili} onChange={toggleTumSec}
                                                style={{ cursor: "pointer" }} />
                                        </th>
                                        <th style={thStyle()} onClick={() => siralamaToggle("symbol")}>
                                            Sembol{siralamaOk("symbol")}
                                        </th>
                                        <th style={thStyle()} onClick={() => siralamaToggle("likidite_skoru")}>
                                            Likidite Skoru{siralamaOk("likidite_skoru")}<BilgiIkonu karti="likidite_skoru" />
                                        </th>
                                        <th style={thStyle()}>Durum<BilgiIkonu karti="durum" /></th>
                                        <th style={thStyle()} onClick={() => siralamaToggle("ort_hacim")}>
                                            Ort. Hacim (20G){siralamaOk("ort_hacim")}<BilgiIkonu karti="ort_hacim" />
                                        </th>
                                        <th style={thStyle()} onClick={() => siralamaToggle("hacim_skor")}>
                                            Hacim Skoru{siralamaOk("hacim_skor")}<BilgiIkonu karti="hacim_skor" />
                                        </th>
                                        <th style={thStyle()} onClick={() => siralamaToggle("trend_etiket")}>
                                            Trend{siralamaOk("trend_etiket")}<BilgiIkonu karti="trend" />
                                        </th>
                                        <th style={thStyle()} onClick={() => siralamaToggle("trend_skor")}>
                                            Trend Skoru{siralamaOk("trend_skor")}<BilgiIkonu karti="trend_skor" />
                                        </th>
                                        <th style={thStyle()} onClick={() => siralamaToggle("slip_pct")}>
                                            Slippage %{siralamaOk("slip_pct")}<BilgiIkonu karti="slip_pct" />
                                        </th>
                                        <th style={thStyle()} onClick={() => siralamaToggle("slip_skor")}>
                                            Slip. Skoru{siralamaOk("slip_skor")}<BilgiIkonu karti="slip_skor" />
                                        </th>
                                        <th style={thStyle()} onClick={() => siralamaToggle("poz_hacim_pct")}>
                                            Poz/Hacim %{siralamaOk("poz_hacim_pct")}<BilgiIkonu karti="poz_hacim" />
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filtreli.map((r, i) => {
                                        const sc       = r.likidite_skoru;
                                        const tc       = trendRenk(r.trend_etiket);
                                        const slc      = slipRenk(r.slip_pct);
                                        const asimVar  = r.poz_hacim_pct > hacimEsigi || r.slip_pct > maxSlippage;
                                        const satirBg  = i % 2 === 0 ? "transparent" : C.bg + "88";

                                        return (
                                            <tr key={r.symbol} style={{
                                                background: satirBg,
                                                borderLeft: asimVar ? `3px solid ${C.warning}` : "3px solid transparent",
                                            }}>
                                                {/* Checkbox */}
                                                <td style={{ ...tdStyle, textAlign: "center" }} onClick={e => e.stopPropagation()}>
                                                    <input type="checkbox" checked={secilenler.has(r.symbol)}
                                                        onChange={() => toggleSec(r.symbol)} style={{ cursor: "pointer" }} />
                                                </td>

                                                {/* Sembol */}
                                                <td style={{ ...tdStyle, fontWeight: 800, color: C.text, fontSize: 13 }}>
                                                    {asimVar && <span title="Eşik aşımı" style={{ marginRight: 4, fontSize: 11 }}>⚠️</span>}
                                                    {r.symbol}
                                                </td>

                                                {/* Likidite Skoru + Çubuk */}
                                                <td style={{ ...tdStyle, minWidth: 130 }}>
                                                    <SkorCubugu val={sc} renk={likiditeSkorRenk(sc)} />
                                                </td>

                                                {/* Durum Rozeti */}
                                                <td style={tdStyle}>
                                                    <LikiditeBadge etiket={r.etiket} skor={sc} />
                                                </td>

                                                {/* Ort. Hacim */}
                                                <td style={{ ...tdStyle, fontWeight: 600 }}>
                                                    {r.ort_hacim_fmt}
                                                </td>

                                                {/* Hacim Skoru */}
                                                <td style={{ ...tdStyle, minWidth: 110 }}>
                                                    <SkorCubugu val={r.hacim_skor} renk={likiditeSkorRenk(r.hacim_skor)} />
                                                </td>

                                                {/* Trend */}
                                                <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>
                                                    <span style={{ color: tc, fontWeight: 700 }}>{r.trend_etiket}</span>
                                                    <span style={{ fontSize: 10, color: C.muted, marginLeft: 5 }}>
                                                        ×{r.trend_oran?.toFixed(2)}
                                                    </span>
                                                </td>

                                                {/* Trend Skoru */}
                                                <td style={{ ...tdStyle, minWidth: 110 }}>
                                                    <SkorCubugu val={r.trend_skor} renk={tc} />
                                                </td>

                                                {/* Slippage % */}
                                                <td style={{
                                                    ...tdStyle, fontWeight: 700, color: slc,
                                                    background: r.slip_pct > maxSlippage ? C.bearish + "18" : "transparent"
                                                }}>
                                                    %{fmt(r.slip_pct, 3)}
                                                    {r.slip_pct > maxSlippage && (
                                                        <span style={{ fontSize: 10, marginLeft: 4 }}>⚠️</span>
                                                    )}
                                                </td>

                                                {/* Slip. Skoru */}
                                                <td style={{ ...tdStyle, minWidth: 110 }}>
                                                    <SkorCubugu val={r.slip_skor} renk={slipRenk(r.slip_pct)} />
                                                </td>

                                                {/* Poz/Hacim % */}
                                                <td style={{
                                                    ...tdStyle, fontWeight: 600,
                                                    color: r.poz_hacim_pct > hacimEsigi ? C.bearish : C.text,
                                                    background: r.poz_hacim_pct > hacimEsigi ? C.bearish + "18" : "transparent"
                                                }}>
                                                    %{fmt(r.poz_hacim_pct, 3)}
                                                    {r.poz_hacim_pct > hacimEsigi && (
                                                        <span style={{ fontSize: 10, marginLeft: 4 }}>⚠️</span>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Footer */}
                    {filtreli.length > 0 && (
                        <div style={{ padding: "10px 16px", borderTop: `1px solid ${C.border}`,
                            display: "flex", gap: 16, alignItems: "center", fontSize: 12, color: C.muted, background: C.bg }}>
                            <span>{filtreli.length} sembol gösteriliyor</span>
                            {[
                                { label: "🟢 Yüksek", min: 80, renk: C.bullish },
                                { label: "🔵 Orta",   min: 60, renk: C.accent  },
                                { label: "🟡 Düşük",  min: 40, renk: C.warning },
                            ].map(({ label, min, renk }) => {
                                const cnt = filtreli.filter(r => r.likidite_skoru >= min &&
                                    (min === 40 ? r.likidite_skoru < 60 : min === 60 ? r.likidite_skoru < 80 : true)).length;
                                if (cnt === 0) return null;
                                return (
                                    <span key={label} style={{ padding: "2px 8px", borderRadius: 20,
                                        background: renk + "22", color: renk, fontSize: 11, fontWeight: 700 }}>
                                        {label}: {cnt}
                                    </span>
                                );
                            })}
                            <span style={{ marginLeft: "auto", fontSize: 11 }}>
                                ⚠️ Sarı kenarlı: hacim veya slippage eşiği aşıyor
                            </span>
                            {sonuc?.tarih && (
                                <span>Son analiz: {new Date(sonuc.tarih).toLocaleString("tr-TR")}</span>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* ── Çoklu Taramaya Gönder ── */}
            {cokluPopup && (
                <CokluTaramaGonderPopup
                    piyasa={piyasa}
                    paraBirimi={paraBirimi}
                    secilenSayisi={filtreli.filter(r => secilenler.has(r.symbol)).length}
                    secilenModul={cokluSecModul}
                    filtre={cokluFiltre}
                    periyotlar={cokluPeriyotlar}
                    onModulSec={m => {
                        setCokluSecModul(m);
                        setCokluFiltre(defaultCokluFiltreler(m, piyasa));
                        const MAP = {
                            "Dip Tarama":"dip_tarama","Likidite Tarama":"likidite_tarama","FVG Tarama":"fvg_tarama",
                            "MSB Tarama":"msb_tarama","OTE Tarama":"ote_tarama","OB & BB Tarama":"obbb_tarama",
                            "IDM Tarama":"idm_tarama","P/D Zone Tarama":"pd_zone_tarama",
                            "Korelasyonel Uyumsuzluk Tarama":"smt_tarama","Killzone Tarama":"killzone_tarama",
                            "Topdown Cascade":"topdown_cascade_tarama","Hacim Profili Tarama":"hacim_profili_tarama",
                            "Hacim Akışı Tarama":"hacim_akisi_tarama","Divergence Tarama":"divergence_tarama",
                        };
                        const dbKey = MAP[m];
                        if (dbKey) getLookupPeriyotlar(dbKey).then(list => setCokluPeriyotlar(list)).catch(() => setCokluPeriyotlar([]));
                        else setCokluPeriyotlar([]);
                    }}
                    onFiltreDegis={setCokluFiltre}
                    onIptal={() => { setCokluPopup(false); setCokluSecModul(null); setCokluFiltre(null); }}
                    onBaslat={() => {
                        if (!cokluSecModul || !cokluFiltre) { toast.error("Modül seçin"); return; }
                        setCokluPopup(false); setCokluOnayPopup(true);
                    }}
                />
            )}

            {/* ── Çoklu Tarama Onay ── */}
            {cokluOnayPopup && (
                <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)",
                    display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
                    <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 14,
                        padding: "1.75rem", width: 420, maxWidth: "95vw",
                        boxShadow: "0 12px 48px rgba(0,0,0,0.5)", color: C.text }}>
                        <div style={{ fontSize: 17, fontWeight: 700, marginBottom: "0.75rem" }}>⚠️ Sayfadan Ayrılıyorsunuz</div>
                        <div style={{ background: "#422006", border: "1px solid #92400e", borderRadius: 8,
                            padding: "1rem", marginBottom: "1.25rem", fontSize: 13, color: "#fde68a", lineHeight: 1.7 }}>
                            Başka bir sayfaya yönlendiriliyorsunuz.{" "}
                            <strong>Bu sayfadaki analiz sonuçları kaybolacak.</strong>
                            <br /><br />Devam etmek istiyor musunuz?
                        </div>
                        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                            <button onClick={() => { setCokluOnayPopup(false); setCokluPopup(true); }}
                                style={PS.btnVazgec}>← Geri Dön</button>
                            <button onClick={() => {
                                const hisseListesi = filtreli.filter(r => secilenler.has(r.symbol)).map(r => r.symbol);
                                setCokluOnayPopup(false);
                                navigate("/coklu-tarama", { state: {
                                    kaynak: "likidite_derinlik", piyasa, paraBirimi,
                                    kaynakModul: "Likidite Derinlik Analizi",
                                    kaynakFiltre: { hacim_esigi: hacimEsigi, max_slippage: maxSlippage },
                                    kaynakSonucHisseler: hisseListesi,
                                    modul: cokluSecModul, filtre: cokluFiltre,
                                    hisseListesi, oto_baslat: true,
                                }});
                            }} style={PS.btnPrimary}>
                                Evet, Devam Et →
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Geçmiş Simülasyon — Kriter ── */}
            {gecmisPopupAdim === "kriter" && (
                <div style={PS.overlay}>
                    <div style={PS.modal}>
                        <div style={PS.baslik}>🔮 Geçmiş Simülasyonu</div>
                        <p style={{ fontSize: 13, color: C.muted, marginBottom: "1.25rem" }}>
                            <strong>{secilenler.size}</strong> sembol seçildi. Simülasyon tarihini belirleyin.
                        </p>
                        <div style={{ marginBottom: "1.25rem" }}>
                            <label style={{ fontSize: 12, fontWeight: 600, color: "#93c5fd", display: "block", marginBottom: 6 }}>
                                📅 Simülasyon Tarihi
                            </label>
                            <input type="date" value={gecmisSimTarihi}
                                max={new Date(Date.now() - 86400000).toISOString().split("T")[0]}
                                onChange={e => setGecmisSimTarihi(e.target.value)}
                                style={{ width: "100%", padding: "9px 12px", borderRadius: 8,
                                    border: `1px solid ${C.border}`, fontSize: 13,
                                    background: C.bg, color: C.text, outline: "none" }} />
                        </div>
                        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                            <button onClick={() => setGecmisPopupAdim(null)} style={PS.btnVazgec}>İptal</button>
                            <button onClick={() => { if (!gecmisSimTarihi) return; setGecmisPopupAdim("onay"); }} style={PS.btnPrimary}>
                                Devam Et →
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Geçmiş Simülasyon — Onay ── */}
            {gecmisPopupAdim === "onay" && (
                <div style={PS.overlay}>
                    <div style={PS.modal}>
                        <div style={PS.baslik}>⚠️ Sayfadan Ayrılıyorsunuz</div>
                        <div style={{ background: "#422006", border: "1px solid #92400e", borderRadius: 8,
                            padding: "1rem", marginBottom: "1.25rem", fontSize: 13, color: "#fde68a", lineHeight: 1.7 }}>
                            Bu sayfa temizlenerek <strong>Geçmiş Simülasyon</strong> sayfasına yönlendirileceksiniz.
                            Seçili semboller için{" "}
                            <strong>{new Date(gecmisSimTarihi + "T00:00:00").toLocaleDateString("tr-TR", { day: "2-digit", month: "long", year: "numeric" })}</strong>{" "}
                            tarihi simülasyonu otomatik başlatılacaktır.
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: "1.25rem", fontSize: 12 }}>
                            {[["Piyasa", piyasa], ["Para Birimi", paraBirimi],
                              ["Sembol Sayısı", `${secilenler.size} sembol`],
                              ["Simülasyon Tarihi", new Date(gecmisSimTarihi + "T00:00:00").toLocaleDateString("tr-TR")],
                            ].map(([k, v]) => (
                                <div key={k} style={{ display: "flex", justifyContent: "space-between",
                                    background: C.bg, borderRadius: 6, padding: "6px 10px" }}>
                                    <span style={{ color: C.muted }}>{k}:</span>
                                    <strong style={{ color: C.text }}>{v}</strong>
                                </div>
                            ))}
                        </div>
                        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                            <button onClick={() => setGecmisPopupAdim("kriter")} style={PS.btnVazgec}>← Geri</button>
                            <button onClick={() => setGecmisPopupAdim(null)} style={PS.btnVazgec}>İptal</button>
                            <button onClick={gecmisSimOnay} style={PS.btnPrimary}>Evet, Başlat 🚀</button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Puanlama — Kriter ── */}
            {puanlamaPopupAdim === "kriter" && (() => {
                const bistOpts   = ["Gün İçi", "Kısa Vade (1-10 gün)", "Orta Vade (11-60 gün)", "Uzun Vade (61+ gün)"];
                const kriptoOpts = ["Kaldıraçlı İşlem", "Gün İçi", "Kısa Vade (1-10 gün)", "Orta Vade (11-60 gün)", "Uzun Vade (61+ gün)"];
                const vadeOpts   = piyasa === "Kripto" ? kriptoOpts : bistOpts;
                const kaldırac   = puanlamaVade === "Kaldıraçlı İşlem";
                return (
                    <div style={PS.overlay}>
                        <div style={{ ...PS.modal, width: 420 }}>
                            <div style={PS.baslik}>🏆 Puanlamaya Gönder</div>
                            <p style={{ fontSize: 13, color: C.muted, marginBottom: "1.25rem" }}>
                                <strong>{secilenler.size}</strong> sembol seçildi. Kriterleri belirleyin.
                            </p>
                            <div style={{ display: "flex", gap: 8, marginBottom: "1.25rem" }}>
                                {[["Piyasa", piyasa], ["Para Birimi", paraBirimi]].map(([et, d]) => (
                                    <div key={et} style={{ flex: 1, background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 14px" }}>
                                        <div style={{ fontSize: 11, color: C.muted, marginBottom: 2 }}>{et} (kilitli)</div>
                                        <div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>🔒 {d}</div>
                                    </div>
                                ))}
                            </div>
                            <div style={{ marginBottom: "1.25rem" }}>
                                <label style={{ fontSize: 12, fontWeight: 600, color: "#93c5fd", display: "block", marginBottom: 6 }}>📅 Vade</label>
                                <select value={puanlamaVade || ""} onChange={e => setPuanlamaVade(e.target.value)}
                                    style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: `1px solid ${C.border}`,
                                        fontSize: 13, background: C.bg, color: C.text, cursor: "pointer", outline: "none" }}>
                                    {vadeOpts.map(o => <option key={o} value={o}>{o}</option>)}
                                </select>
                            </div>
                            {kaldırac && (
                                <div style={{ marginBottom: "1.25rem" }}>
                                    <div style={{ fontSize: 12, fontWeight: 600, color: "#93c5fd", marginBottom: 8 }}>📈 İşlem Yönü</div>
                                    <div style={{ display: "flex", gap: 8 }}>
                                        {["Long", "Short"].map(y => (
                                            <button key={y} type="button" onClick={() => togglePuanlamaYon(y)}
                                                style={{ flex: 1, padding: 10, borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: 600,
                                                    border: puanlamaYon.has(y) ? `2px solid ${C.accent}` : `2px solid ${C.border}`,
                                                    background: puanlamaYon.has(y) ? (y === "Long" ? "#052e16" : "#450a0a") : "transparent",
                                                    color: puanlamaYon.has(y) ? (y === "Long" ? "#4ade80" : "#f87171") : C.muted }}>
                                                {y === "Long" ? "📈 Long" : "📉 Short"}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                            <div style={{ marginBottom: "1.5rem" }}>
                                <label style={{ fontSize: 12, fontWeight: 600, color: "#93c5fd", display: "block", marginBottom: 8 }}>🎯 Minimum Puan</label>
                                <div style={{ display: "flex", gap: 6 }}>
                                    {[0, 25, 50, 60, 75].map(p => (
                                        <button key={p} type="button" onClick={() => setPuanlamaMinPuan(p)}
                                            style={{ flex: 1, padding: "8px 4px", borderRadius: 8, cursor: "pointer", fontSize: 13,
                                                fontWeight: puanlamaMinPuan === p ? 700 : 400,
                                                border: puanlamaMinPuan === p ? `2px solid ${C.accent}` : `2px solid ${C.border}`,
                                                background: puanlamaMinPuan === p ? "#2563eb" : "transparent",
                                                color: puanlamaMinPuan === p ? "white" : C.text }}>
                                            {p === 0 ? "0+" : `${p}+`}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                                <button onClick={() => setPuanlamaPopupAdim(null)} style={PS.btnVazgec}>İptal</button>
                                <button onClick={() => setPuanlamaPopupAdim("onay")} style={PS.btnPrimary}>Devam Et →</button>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* ── Puanlama — Onay ── */}
            {puanlamaPopupAdim === "onay" && (
                <div style={PS.overlay}>
                    <div style={{ ...PS.modal, width: 440 }}>
                        <div style={PS.baslik}>⚠️ Sayfadan Ayrılıyorsunuz</div>
                        <div style={{ background: "#422006", border: "1px solid #92400e", borderRadius: 8,
                            padding: "1rem", marginBottom: "1.25rem", fontSize: 13, color: "#fde68a", lineHeight: 1.7 }}>
                            Başka bir sayfaya yönlendiriliyorsunuz. <strong>Ekrandaki analiz sonuçları kaybolacak.</strong>
                            <br /><br />Seçili <strong>{secilenler.size} sembol</strong> için{" "}
                            <strong>Puanlama ve Tahmin</strong> taraması otomatik olarak başlatılacaktır. Emin misiniz?
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: "1.25rem", fontSize: 12 }}>
                            {[["Piyasa", piyasa], ["Para Birimi", paraBirimi],
                              ["Vade", puanlamaVade],
                              ...(puanlamaVade === "Kaldıraçlı İşlem" ? [["İşlem Yönü", [...puanlamaYon].join(" & ")]] : []),
                              ["Min Puan", puanlamaMinPuan === 0 ? "0+ (Tümü)" : `${puanlamaMinPuan}+`],
                              ["Sembol Sayısı", `${secilenler.size} sembol`],
                            ].map(([k, v]) => (
                                <div key={k} style={{ display: "flex", justifyContent: "space-between",
                                    background: C.bg, borderRadius: 6, padding: "6px 10px" }}>
                                    <span style={{ color: C.muted }}>{k}:</span>
                                    <strong style={{ color: C.text }}>{v}</strong>
                                </div>
                            ))}
                        </div>
                        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                            <button onClick={() => setPuanlamaPopupAdim("kriter")} style={PS.btnVazgec}>← Geri</button>
                            <button onClick={() => setPuanlamaPopupAdim(null)} style={PS.btnVazgec}>Hayır, İptal</button>
                            <button onClick={() => {
                                const hisseListesi = filtreli.filter(r => secilenler.has(r.symbol)).map(r => r.symbol);
                                setPuanlamaPopupAdim(null);
                                navigate("/puanlama-tahmin", { state: {
                                    kaynak: "likidite_derinlik", piyasa, para_birimi: paraBirimi,
                                    vade: puanlamaVade, islem_yonu: [...puanlamaYon].join(","),
                                    min_puan: puanlamaMinPuan, hisse_listesi: hisseListesi, oto_baslat: true,
                                }});
                            }} style={PS.btnPrimary}>
                                Evet, Başlat 🚀
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Alarm Popup ── */}
            {alarmPopup && (
                <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)",
                    display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
                    <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 14,
                        padding: "1.75rem", width: 460, maxWidth: "95vw", maxHeight: "85vh", overflowY: "auto",
                        boxShadow: "0 12px 48px rgba(0,0,0,0.5)", color: C.text }}>
                        <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>⏰ Alarm Ekle</div>
                        <p style={{ fontSize: 13, color: C.muted, marginBottom: 16 }}>
                            <strong>{alarmHisseler.length}</strong> sembol için hedef fiyat girin.
                        </p>
                        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 16 }}>
                            {alarmHisseler.map(h => (
                                <div key={h.symbol} style={{ background: C.bg, border: `1px solid ${C.border}`,
                                    borderRadius: 8, padding: "10px 12px" }}>
                                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                                        <strong style={{ fontSize: 14, color: C.accent }}>{h.symbol}</strong>
                                        <span style={{ color: C.muted, fontSize: 12 }}>{piyasa} · {paraBirimi}</span>
                                    </div>
                                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                        <span style={{ color: C.muted, fontSize: 12, minWidth: 80 }}>Hedef Fiyat:</span>
                                        <input type="number" placeholder="0.00"
                                            value={hedefFiyatlar[h.symbol] || ""}
                                            onChange={e => setHedefFiyatlar(p => ({ ...p, [h.symbol]: e.target.value }))}
                                            style={{ flex: 1, padding: "5px 9px", borderRadius: 6,
                                                border: `1px solid ${C.border}`, background: C.panel,
                                                color: C.text, fontSize: 13, textAlign: "right", outline: "none" }} />
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div style={{ display: "flex", gap: 16, marginBottom: 20 }}>
                            {[["Mail", alarmMail, setAlarmMail], ["Pop-up", alarmBildirim, setAlarmBildirim]].map(([ad, val, setter]) => (
                                <label key={ad} style={{ display: "flex", alignItems: "center", gap: 6,
                                    fontSize: 13, cursor: "pointer", color: C.text }}>
                                    <input type="checkbox" checked={val} onChange={e => setter(e.target.checked)} /> {ad}
                                </label>
                            ))}
                        </div>
                        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                            <button onClick={() => { setAlarmPopup(false); setAlarmHisseler([]); }}
                                style={{ padding: "8px 18px", borderRadius: 8, border: `1px solid ${C.border}`,
                                    background: "transparent", cursor: "pointer", fontSize: 13, color: C.muted }}>
                                Vazgeç
                            </button>
                            <button onClick={alarmKaydet} disabled={alarmKaydediliyor}
                                style={{ padding: "8px 20px", borderRadius: 8, border: "none",
                                    background: alarmKaydediliyor ? C.border : "linear-gradient(135deg,#1d4ed8,#3b82f6)",
                                    color: "white", cursor: alarmKaydediliyor ? "not-allowed" : "pointer",
                                    fontSize: 13, fontWeight: 600 }}>
                                {alarmKaydediliyor ? "Kaydediliyor..." : "Alarmları Kaydet"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Menü dışı tıklama kapayıcı */}
            {islemMenuAcik && (
                <div style={{ position: "fixed", inset: 0, zIndex: 50 }}
                    onClick={() => setIslemMenuAcik(false)} />
            )}

            {/* ── Boş Ekran ── */}
            {!sonuc && !calisiyor && (
                <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 12,
                    padding: "64px 24px", textAlign: "center" }}>
                    <div style={{ fontSize: 56, marginBottom: 16 }}>💧</div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: C.text, marginBottom: 8 }}>
                        Likidite Derinlik Analizi
                    </div>
                    <div style={{ fontSize: 14, color: C.muted, maxWidth: 480, margin: "0 auto", lineHeight: 1.7 }}>
                        Slippage ve hacim eşiklerini ayarlayın, ardından analizi başlatın.
                        Düşük hacimli BIST hisseleri ve küçük kriptolarda pozisyon
                        piyasayı hareket ettirebilir — bu modül o riski ölçer.
                    </div>
                </div>
            )}

        </div>
        </div>
    );
}

// ─── Popup Stilleri ──────────────────────────────────────────────────────────
const PS = {
    overlay:    { position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 },
    modal:      { background: "#1e293b", border: "1px solid #334155", borderRadius: 14, padding: "2rem", width: 480, maxWidth: "95vw", boxShadow: "0 12px 48px rgba(0,0,0,0.5)", color: "#e2e8f0" },
    baslik:     { fontSize: 18, fontWeight: 700, color: "#e2e8f0", marginBottom: "0.75rem" },
    btnVazgec:  { padding: "8px 18px", borderRadius: 8, border: "1px solid #334155", background: "transparent", cursor: "pointer", fontSize: 13, color: "#94a3b8" },
    btnPrimary: { padding: "8px 20px", borderRadius: 8, border: "none", background: "linear-gradient(135deg,#1d4ed8,#3b82f6)", color: "white", cursor: "pointer", fontSize: 13, fontWeight: 600 },
};

// ─── Çoklu Tarama Gönder Yardımcıları ────────────────────────────────────────

const COKLU_MODULLER = [
    { label: "📊 Dip Tarama",            value: "Dip Tarama",                     pasif: false },
    { label: "🌊 Likidite Tarama",        value: "Likidite Tarama",                pasif: false },
    { label: "📈 FVG Tarama",             value: "FVG Tarama",                     pasif: false },
    { label: "⚓ Çapa Noktası Tarama",    value: "Çapa Noktası Tarama",            pasif: false },
    { label: "📐 MSB Tarama",             value: "MSB Tarama",                     pasif: false },
    { label: "🎯 OTE Tarama",             value: "OTE Tarama",                     pasif: false },
    { label: "🧱 OB/BB Tarama",           value: "OB & BB Tarama",                 pasif: false },
    { label: "🎣 IDM Tarama",             value: "IDM Tarama",                     pasif: false },
    { label: "⚖️ P/D Zone Tarama",        value: "P/D Zone Tarama",               pasif: false },
    { label: "🔀 SMT Tarama",             value: "Korelasyonel Uyumsuzluk Tarama", pasif: false },
    { label: "⏰ Killzone Tarama",         value: "Killzone Tarama",               pasif: false },
    { label: "🌿 Topdown Cascade",         value: "Topdown Cascade",               pasif: false },
    { label: "📊 Hacim Profili Tarama",    value: "Hacim Profili Tarama",          pasif: false },
    { label: "🌊 Hacim Akışı Tarama",      value: "Hacim Akışı Tarama",            pasif: false },
    { label: "📉 Divergence Tarama",       value: "Divergence Tarama",             pasif: false },
];

const _PERIYOT_OPTS = ["1 Gün", "1 Hafta", "1 Ay", "3 Aylık", "6 Aylık", "Yıllık"];
const _KZ_LISTESI   = [
    "Asya KZ (03:00-09:00)", "Londra Açılış (10:00-12:00)", "New York Açılış (15:00-17:00)",
    "Londra Kapanış (18:00-20:00)", "BIST Açılış (10:00-11:00)",
    "BIST Öğle Dönüşü (13:00-14:00)", "BIST Kapanış Öncesi (17:00-18:00)",
];

function defaultCokluFiltreler(modul, piyasa) {
    switch (modul) {
        case "Dip Tarama":       return { tarama_periyot: "Aylık", tolerans: 1 };
        case "Likidite Tarama":  return { sweep_tipler: { "Boğa Sweep": true, "Ayı Sweep": false }, tarama_periyot: "1 Gün", lookback: 20, wick_oran: 50 };
        case "FVG Tarama":       return { fvg_tipler: { "Boğa FVG": true, "Ayı FVG": false, "iFVG": false }, bpr_kontrol: "Hayır", tarama_periyot: "1 Gün", mum_sayisi: 100 };
        case "MSB Tarama":       return { msb_tipler: { "Boğa MSB": true, "Ayı MSB": false }, tarama_periyot: "1 Ay", swing_pencere: 3 };
        case "OTE Tarama":       return { ote_tipler: { "Boğa OTE": true, "Ayı OTE": false }, tarama_periyot: "1 Hafta", swing_pencere: 3, mum_sayisi: 100 };
        case "OB & BB Tarama":   return { obbb_tipler: { "Boğa OB": true, "Ayı OB": false, "Boğa BB": false, "Ayı BB": false }, tarama_periyot: "1 Gün", mum_sayisi: 200, govde_oran: 60, sadece_taze: true };
        case "IDM Tarama":       return { idm_tipler: { "Boğa IDM": true, "Ayı IDM": false }, tarama_periyot: "1 Gün", mum_sayisi: 200 };
        case "P/D Zone Tarama":  return { bolge_filtre: { "Discount": true, "Equilibrium": false, "Premium": false }, tarama_periyot: "1 Gün", range_window: 20, mum_sayisi: 200 };
        case "Korelasyonel Uyumsuzluk Tarama": return { smt_tipler: { "Boğa SMT": true, "Ayı SMT": false }, tarama_periyot: "1 Gün", referans_varlik: piyasa === "BIST" ? "XU100 (BIST100)" : "BTC", swing_pencere: 3, kor_pencere: 50, min_korelasyon: 70, mum_sayisi: 200 };
        case "Killzone Tarama":  return { killzoneler: { "BIST Açılış (10:00-11:00)": true }, yapi_tipleri: { "FVG": true, "MSB": false, "Sweep": false }, geri_bak_gun: 5 };
        case "Topdown Cascade":  return { topdown_mod: piyasa };
        case "Hacim Profili Tarama": return { tarama_periyot: "1 Gün", sinyal_filtre: [], min_skor: 0, mum_sayisi: 300 };
        case "Hacim Akışı Tarama":   return { lookback: 20, min_spike: 1.5, sinyal_filtre: {"📈 Güçlü Alım":true,"🔶 Orta Alım":true,"⬇️ Zayıf Alım":false,"📉 Güçlü Satım":true,"🔶 Orta Satım":true,"⬆️ Zayıf Satım":false,"⚖️ Belirsiz":false} };
        case "Divergence Tarama":    return { tarama_periyot: "1 Gün", pivot_modu: "fractal", osilatorler: {"RSI":true,"MACD Histogram":true,"Stokastik RSI":true,"OBV":false}, divergence_tipleri: {"Regular Bullish":true,"Regular Bearish":true,"Hidden Bullish":false,"Hidden Bearish":false}, swing_pencere:5, zigzag_esik:1, n_son_pivot:2, min_pivot_mesafe:3, min_hareket_atr:0, rsi_periyot:14, macd_fast:12, macd_slow:26, macd_signal:9, stoch_rsi_periyot:14, stoch_periyot:14, stoch_k:3, stoch_d:3, ayarlar_acik:false };
        default: return {};
    }
}

function CokluTaramaGonderPopup({ piyasa, paraBirimi, secilenSayisi, secilenModul, filtre, periyotlar, onModulSec, onFiltreDegis, onIptal, onBaslat }) {
    const periyotOpts = periyotlar && periyotlar.length > 0 ? periyotlar : _PERIYOT_OPTS;
    const set = (key, val) => onFiltreDegis({ ...filtre, [key]: val });
    const ck  = (grup, key) => onFiltreDegis({ ...filtre, [grup]: { ...filtre[grup], [key]: !filtre[grup][key] } });

    const CheckGrup = ({ alan, etiket }) => (
        <div style={{ marginBottom: "10px" }}>
            <div style={{ fontSize: "12px", fontWeight: "600", color: "#93c5fd", marginBottom: "6px" }}>{etiket}</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                {Object.keys(filtre[alan] || {}).map(k => (
                    <label key={k} style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "12px", cursor: "pointer", color: "#e2e8f0" }}>
                        <input type="checkbox" checked={!!filtre[alan][k]} onChange={() => ck(alan, k)} />{k}
                    </label>
                ))}
            </div>
        </div>
    );

    const Sel = ({ alan, etiket, opts, labelFn }) => (
        <div style={{ marginBottom: "10px" }}>
            <div style={{ fontSize: "12px", fontWeight: "600", color: "#93c5fd", marginBottom: "6px" }}>{etiket}</div>
            <select value={filtre[alan]} onChange={e => set(alan, isNaN(e.target.value) ? e.target.value : Number(e.target.value))}
                style={{ padding: "6px 10px", borderRadius: "7px", border: "1px solid #334155", background: "#0f172a", color: "#e2e8f0", fontSize: "13px", width: "100%" }}>
                {opts.map(o => <option key={o} value={o}>{labelFn ? labelFn(o) : o}</option>)}
            </select>
        </div>
    );

    const renderFiltreler = () => {
        if (!filtre) return null;
        switch (secilenModul) {
            case "Dip Tarama":       return (<><Sel alan="tarama_periyot" etiket="Periyot" opts={periyotOpts} /><Sel alan="tolerans" etiket="Tolerans" opts={[1,3,5]} labelFn={v=>`%${v}`} /></>);
            case "Likidite Tarama":  return (<><CheckGrup alan="sweep_tipler" etiket="Sweep Tipi" /><Sel alan="tarama_periyot" etiket="Periyot" opts={periyotOpts} /><Sel alan="lookback" etiket="Lookback" opts={[10,20,50,100]} labelFn={v=>`${v} Mum`} /><Sel alan="wick_oran" etiket="Min Fitil" opts={[30,40,50,60,70]} labelFn={v=>`%${v}`} /></>);
            case "FVG Tarama":       return (<><CheckGrup alan="fvg_tipler" etiket="FVG Tipi" /><Sel alan="bpr_kontrol" etiket="BPR Kontrol" opts={["Evet","Hayır"]} /><Sel alan="tarama_periyot" etiket="Tarama Periyodu" opts={periyotOpts} /><Sel alan="mum_sayisi" etiket="Mum Sayısı" opts={[25,50,100,150,200,250,300,350,400,500,600,700]} /></>);
            case "MSB Tarama":       return (<><CheckGrup alan="msb_tipler" etiket="MSB Tipi" /><Sel alan="tarama_periyot" etiket="Periyot" opts={periyotOpts} /><Sel alan="swing_pencere" etiket="Swing Penceresi" opts={[2,3,4,5,6,7,8]} labelFn={v=>`${v} Mum`} /></>);
            case "OTE Tarama":       return (<><CheckGrup alan="ote_tipler" etiket="OTE Tipi" /><Sel alan="tarama_periyot" etiket="Tarama Periyodu" opts={periyotOpts} /><Sel alan="swing_pencere" etiket="Swing Penceresi" opts={[2,3,4,5,6,7,8]} labelFn={v=>`${v} Mum`} /><Sel alan="mum_sayisi" etiket="Mum Sayısı" opts={[50,100,150,200,300]} /></>);
            case "OB & BB Tarama":   return (<><CheckGrup alan="obbb_tipler" etiket="OB/BB Tipi" /><Sel alan="tarama_periyot" etiket="Periyot" opts={periyotOpts} /><Sel alan="mum_sayisi" etiket="Mum Sayısı" opts={[100,200,300,500]} /><Sel alan="govde_oran" etiket="Min Gövde" opts={[40,50,60,70]} labelFn={v=>`%${v}`} /><div style={{marginBottom:"10px"}}><label style={{display:"flex",alignItems:"center",gap:"6px",fontSize:"12px",cursor:"pointer",color:"#e2e8f0"}}><input type="checkbox" checked={filtre.sadece_taze} onChange={e=>set("sadece_taze",e.target.checked)} />Sadece Taze</label></div></>);
            case "IDM Tarama":       return (<><CheckGrup alan="idm_tipler" etiket="IDM Tipi" /><Sel alan="tarama_periyot" etiket="Periyot" opts={periyotOpts} /><Sel alan="mum_sayisi" etiket="Mum Sayısı" opts={[100,200,300,500]} /></>);
            case "P/D Zone Tarama":  return (<><CheckGrup alan="bolge_filtre" etiket="Bölge" /><Sel alan="tarama_periyot" etiket="Periyot" opts={periyotOpts} /><Sel alan="range_window" etiket="Range Penceresi" opts={[10,20,50,100]} labelFn={v=>`${v} Mum`} /><Sel alan="mum_sayisi" etiket="Mum Sayısı" opts={[100,200,300,500]} /></>);
            case "Korelasyonel Uyumsuzluk Tarama": return (<><CheckGrup alan="smt_tipler" etiket="SMT Tipi" /><Sel alan="tarama_periyot" etiket="Periyot" opts={periyotOpts} /><div style={{marginBottom:"10px"}}><div style={{fontSize:"12px",fontWeight:"600",color:"#93c5fd",marginBottom:"6px"}}>Referans Varlık</div><select value={filtre.referans_varlik} onChange={e=>set("referans_varlik",e.target.value)} style={{padding:"6px 10px",borderRadius:"7px",border:"1px solid #334155",background:"#0f172a",color:"#e2e8f0",fontSize:"13px",width:"100%"}}>{(piyasa==="BIST"?["XU100 (BIST100)","XU030 (BIST30)"]:["BTC","ETH","BNB"]).map(v=><option key={v}>{v}</option>)}</select></div><Sel alan="swing_pencere" etiket="Swing Penceresi" opts={[2,3,5]} labelFn={v=>`${v} Mum`} /><Sel alan="min_korelasyon" etiket="Min Korelasyon" opts={[50,60,70,80,90]} labelFn={v=>`%${v}`} /><Sel alan="mum_sayisi" etiket="Mum Sayısı" opts={[100,200,300]} /></>);
            case "Killzone Tarama":  return (<><div style={{marginBottom:"10px"}}><div style={{fontSize:"12px",fontWeight:"600",color:"#93c5fd",marginBottom:"6px"}}>Killzone</div><div style={{display:"flex",flexWrap:"wrap",gap:"8px"}}>{_KZ_LISTESI.filter(k=>piyasa==="BIST"||!k.includes("BIST")).map(k=><label key={k} style={{display:"flex",alignItems:"center",gap:"4px",fontSize:"12px",cursor:"pointer",color:"#e2e8f0"}}><input type="checkbox" checked={!!filtre.killzoneler[k]} onChange={()=>ck("killzoneler",k)} />{k.split("(")[0].trim()}</label>)}</div></div><div style={{marginBottom:"10px"}}><div style={{fontSize:"12px",fontWeight:"600",color:"#93c5fd",marginBottom:"6px"}}>Yapı Tipi</div><div style={{display:"flex",gap:"12px"}}>{["FVG","MSB","Sweep"].map(k=><label key={k} style={{display:"flex",alignItems:"center",gap:"4px",fontSize:"12px",cursor:"pointer",color:"#e2e8f0"}}><input type="checkbox" checked={!!filtre.yapi_tipleri[k]} onChange={()=>ck("yapi_tipleri",k)} />{k}</label>)}</div></div><Sel alan="geri_bak_gun" etiket="Geriye Bakış" opts={[1,3,5,10]} labelFn={v=>`Son ${v} Gün`} /></>);
            case "Topdown Cascade":  return (<div style={{fontSize:"13px",color:"#94a3b8",padding:"8px 0"}}>{piyasa} — EMA200 + 4H Sweep taraması. Ek kriter seçimi gerekmez.</div>);
            case "Hacim Profili Tarama": {
                const HP_SF = ["POC Yakını","Discount","Premium","HVN Yakın","LVN İçi"];
                const hpSF = filtre.sinyal_filtre || [];
                const toggleHpSF = v => { const sf = Array.isArray(hpSF)?hpSF:[]; set("sinyal_filtre", sf.includes(v)?sf.filter(x=>x!==v):[...sf,v]); };
                return (<><Sel alan="tarama_periyot" etiket="Periyot" opts={periyotOpts} /><div style={{marginBottom:"10px"}}><div style={{fontSize:"12px",fontWeight:"600",color:"#93c5fd",marginBottom:"6px"}}>Sinyal Filtresi</div><div style={{display:"flex",flexWrap:"wrap",gap:"6px"}}>{HP_SF.map(v=>{const aktif=Array.isArray(hpSF)&&hpSF.includes(v);return(<button key={v} type="button" onClick={()=>toggleHpSF(v)} style={{padding:"3px 10px",borderRadius:20,fontSize:11,cursor:"pointer",fontWeight:aktif?600:400,border:`1px solid ${aktif?"#3b82f6":"#334155"}`,background:aktif?"#3b82f622":"transparent",color:aktif?"#3b82f6":"#94a3b8"}}>{v}</button>);})}</div></div><Sel alan="min_skor" etiket="Min Skor" opts={[0,2,3,4,5,6,7]} labelFn={v=>v===0?"Tümü":`≥${v}`} /><Sel alan="mum_sayisi" etiket="Mum Sayısı" opts={[100,200,300,500]} /></>);
            }
            case "Hacim Akışı Tarama": {
                const HA_RENK_MAP = {"📈 Güçlü Alım":"#16a34a","🔶 Orta Alım":"#84cc16","⬇️ Zayıf Alım":"#a3e635","📉 Güçlü Satım":"#dc2626","🔶 Orta Satım":"#f97316","⬆️ Zayıf Satım":"#fca5a5","⚖️ Belirsiz":"#9ca3af"};
                const haSF = (typeof filtre.sinyal_filtre==="object"&&!Array.isArray(filtre.sinyal_filtre)) ? filtre.sinyal_filtre : {};
                const toggleHA = s => set("sinyal_filtre",{...haSF,[s]:!haSF[s]});
                return (<><div style={{marginBottom:"10px"}}><div style={{fontSize:"12px",fontWeight:"600",color:"#93c5fd",marginBottom:"6px"}}>Ort. Pencere (Gün)</div><select value={filtre.lookback||20} onChange={e=>set("lookback",Number(e.target.value))} style={{padding:"6px 10px",borderRadius:"7px",border:"1px solid #334155",background:"#0f172a",color:"#e2e8f0",fontSize:"13px",width:"100%"}}>{[5,10,20,30,40,50].map(v=><option key={v} value={v}>{v} Gün</option>)}</select></div><div style={{marginBottom:"10px"}}><div style={{fontSize:"12px",fontWeight:"600",color:"#93c5fd",marginBottom:"6px"}}>Min. Spike (×Ort.)</div><input type="number" value={filtre.min_spike||1.5} min={1.0} max={10} step={0.1} onChange={e=>set("min_spike",parseFloat(e.target.value))} style={{padding:"6px 10px",borderRadius:"7px",border:"1px solid #334155",background:"#0f172a",color:"#e2e8f0",fontSize:"13px",width:"80px"}}/></div><div style={{marginBottom:"10px"}}><div style={{fontSize:"12px",fontWeight:"600",color:"#93c5fd",marginBottom:"6px"}}>Sinyal Tipi</div><div style={{display:"flex",gap:8}}><div style={{background:"#0f172a",border:"1px solid #16a34a55",borderRadius:6,padding:"6px 10px",flex:1}}><div style={{fontSize:10,fontWeight:700,color:"#16a34a",marginBottom:4}}>📈 ALIMLAR</div>{["📈 Güçlü Alım","🔶 Orta Alım","⬇️ Zayıf Alım","⚖️ Belirsiz"].map(s=>{const aktif=!!haSF[s];return(<button key={s} type="button" onClick={()=>toggleHA(s)} style={{display:"block",width:"100%",textAlign:"left",padding:"2px 6px",borderRadius:4,marginBottom:2,cursor:"pointer",fontSize:11,fontWeight:aktif?700:400,background:aktif?HA_RENK_MAP[s]+"22":"transparent",border:`1px solid ${aktif?HA_RENK_MAP[s]:"transparent"}`,color:aktif?HA_RENK_MAP[s]:"#94a3b8"}}>{s}</button>);})}</div><div style={{background:"#0f172a",border:"1px solid #dc262655",borderRadius:6,padding:"6px 10px",flex:1}}><div style={{fontSize:10,fontWeight:700,color:"#dc2626",marginBottom:4}}>📉 SATIMLAR</div>{["📉 Güçlü Satım","🔶 Orta Satım","⬆️ Zayıf Satım"].map(s=>{const aktif=!!haSF[s];return(<button key={s} type="button" onClick={()=>toggleHA(s)} style={{display:"block",width:"100%",textAlign:"left",padding:"2px 6px",borderRadius:4,marginBottom:2,cursor:"pointer",fontSize:11,fontWeight:aktif?700:400,background:aktif?HA_RENK_MAP[s]+"22":"transparent",border:`1px solid ${aktif?HA_RENK_MAP[s]:"transparent"}`,color:aktif?HA_RENK_MAP[s]:"#94a3b8"}}>{s}</button>);})}</div></div></div></>);
            }
            case "Divergence Tarama": {
                const DIV_OSC = ["RSI","MACD Histogram","Stokastik RSI","OBV"];
                const DIV_TIPLER = [{key:"Regular Bullish",ikon:"📈",renk:"#16a34a"},{key:"Regular Bearish",ikon:"📉",renk:"#dc2626"},{key:"Hidden Bullish",ikon:"🔼",renk:"#06b6d4"},{key:"Hidden Bearish",ikon:"🔽",renk:"#f59e0b"}];
                const divOsc = filtre.osilatorler || {};
                const divTip = filtre.divergence_tipleri || {};
                const toggleOsc = o => set("osilatorler",{...divOsc,[o]:!divOsc[o]});
                const toggleTip = t => set("divergence_tipleri",{...divTip,[t]:!divTip[t]});
                const ayarlarAcik = filtre.ayarlar_acik||false;
                const stochAktif = !!divOsc["Stokastik RSI"], macdAktif = !!divOsc["MACD Histogram"], rsiAktif = !!divOsc["RSI"];
                return (<><Sel alan="tarama_periyot" etiket="Periyot" opts={periyotOpts} /><div style={{marginBottom:"10px"}}><div style={{fontSize:"12px",fontWeight:"600",color:"#93c5fd",marginBottom:"6px"}}>Osilatörler</div><div style={{display:"flex",flexWrap:"wrap",gap:6}}>{DIV_OSC.map(o=>{const aktif=!!divOsc[o];return(<button key={o} type="button" onClick={()=>toggleOsc(o)} style={{padding:"4px 10px",borderRadius:8,fontSize:11,cursor:"pointer",fontWeight:aktif?600:400,border:`1px solid ${aktif?"#3b82f6":"#334155"}`,background:aktif?"#3b82f618":"#0f172a",color:aktif?"#3b82f6":"#94a3b8"}}>{o}</button>);})}</div></div><div style={{marginBottom:"10px"}}><div style={{fontSize:"12px",fontWeight:"600",color:"#93c5fd",marginBottom:"6px"}}>Divergence Tipleri</div><div style={{display:"flex",flexWrap:"wrap",gap:6}}>{DIV_TIPLER.map(t=>{const aktif=!!divTip[t.key];return(<button key={t.key} type="button" onClick={()=>toggleTip(t.key)} style={{padding:"4px 10px",borderRadius:8,fontSize:11,cursor:"pointer",fontWeight:aktif?600:400,border:`1px solid ${aktif?t.renk:"#334155"}`,background:aktif?t.renk+"18":"#0f172a",color:aktif?t.renk:"#94a3b8"}}>{t.ikon} {t.key}</button>);})}</div></div><button type="button" onClick={()=>set("ayarlar_acik",!ayarlarAcik)} style={{padding:"5px 12px",borderRadius:8,border:"1px solid #334155",background:ayarlarAcik?"#334155":"#0f172a",color:"#e2e8f0",fontSize:11,fontWeight:600,cursor:"pointer",marginBottom:8}}>⚙️ Gelişmiş Ayarlar {ayarlarAcik?"▲":"▼"}</button>{ayarlarAcik&&(<div style={{display:"flex",flexWrap:"wrap",gap:8,marginBottom:8}}>{(filtre.pivot_modu||"fractal")==="fractal"?(<div><div style={{fontSize:11,color:"#93c5fd",marginBottom:4}}>Swing Penceresi</div><input type="number" value={filtre.swing_pencere||5} min={2} max={20} onChange={e=>set("swing_pencere",parseInt(e.target.value))} style={{padding:"4px 8px",borderRadius:6,border:"1px solid #334155",background:"#0f172a",color:"#e2e8f0",fontSize:12,width:65}}/></div>):(<div><div style={{fontSize:11,color:"#93c5fd",marginBottom:4}}>ZigZag Eşik (%)</div><input type="number" value={filtre.zigzag_esik||1} min={0.5} max={20} step={0.5} onChange={e=>set("zigzag_esik",parseFloat(e.target.value))} style={{padding:"4px 8px",borderRadius:6,border:"1px solid #334155",background:"#0f172a",color:"#e2e8f0",fontSize:12,width:65}}/></div>)}{rsiAktif&&<div><div style={{fontSize:11,color:"#93c5fd",marginBottom:4}}>RSI Periyot</div><input type="number" value={filtre.rsi_periyot||14} min={2} max={50} onChange={e=>set("rsi_periyot",parseInt(e.target.value))} style={{padding:"4px 8px",borderRadius:6,border:"1px solid #334155",background:"#0f172a",color:"#e2e8f0",fontSize:12,width:65}}/></div>}{macdAktif&&<><div><div style={{fontSize:11,color:"#93c5fd",marginBottom:4}}>MACD Fast</div><input type="number" value={filtre.macd_fast||12} min={2} max={50} onChange={e=>set("macd_fast",parseInt(e.target.value))} style={{padding:"4px 8px",borderRadius:6,border:"1px solid #334155",background:"#0f172a",color:"#e2e8f0",fontSize:12,width:65}}/></div><div><div style={{fontSize:11,color:"#93c5fd",marginBottom:4}}>MACD Slow</div><input type="number" value={filtre.macd_slow||26} min={5} max={100} onChange={e=>set("macd_slow",parseInt(e.target.value))} style={{padding:"4px 8px",borderRadius:6,border:"1px solid #334155",background:"#0f172a",color:"#e2e8f0",fontSize:12,width:65}}/></div></>}{stochAktif&&<><div><div style={{fontSize:11,color:"#93c5fd",marginBottom:4}}>StochRSI Periyot</div><input type="number" value={filtre.stoch_rsi_periyot||14} min={2} max={50} onChange={e=>set("stoch_rsi_periyot",parseInt(e.target.value))} style={{padding:"4px 8px",borderRadius:6,border:"1px solid #334155",background:"#0f172a",color:"#e2e8f0",fontSize:12,width:65}}/></div></>}</div>)}</>);
            }
            default: return null;
        }
    };

    return (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.55)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000}}>
            <div style={{background:"#1e293b",border:"1px solid #334155",borderRadius:"14px",padding:"1.75rem",width:"500px",maxWidth:"95vw",maxHeight:"85vh",overflowY:"auto",boxShadow:"0 12px 48px rgba(0,0,0,0.5)",color:"#e2e8f0"}}>
                <div style={{fontSize:"18px",fontWeight:"700",color:"#e2e8f0",marginBottom:"0.5rem"}}>🔗 Çoklu Taramaya Gönder</div>
                <p style={{fontSize:"13px",color:"#94a3b8",marginBottom:"1.25rem"}}>
                    <strong>{secilenSayisi}</strong> sembol seçili. Devam edilecek modülü ve kriterleri seçin.
                </p>
                <div style={{marginBottom:"1.25rem"}}>
                    <div style={{fontSize:"12px",fontWeight:"600",color:"#93c5fd",marginBottom:"8px"}}>Modül Seçin</div>
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"6px"}}>
                        {COKLU_MODULLER.map(m => (
                            <button key={m.value} type="button" disabled={m.pasif} onClick={() => !m.pasif && onModulSec(m.value)}
                                style={{padding:"8px 10px",borderRadius:"8px",border:"2px solid",
                                    borderColor:secilenModul===m.value?"#3b82f6":m.pasif?"#1e293b":"#334155",
                                    background:secilenModul===m.value?"#1d4ed822":m.pasif?"#0f172a":"#0f172a",
                                    color:m.pasif?"#475569":secilenModul===m.value?"#93c5fd":"#e2e8f0",
                                    cursor:m.pasif?"default":"pointer",
                                    fontSize:"12px",fontWeight:secilenModul===m.value?"600":"400",textAlign:"left"}}>
                                {m.label}
                                {m.pasif && <span style={{fontSize:"10px",marginLeft:"4px",color:"#475569"}}>(Mevcut)</span>}
                            </button>
                        ))}
                    </div>
                </div>
                {secilenModul && (
                    <div style={{background:"#0f172a",border:"1px solid #334155",borderRadius:"8px",padding:"10px 14px",marginBottom:"1rem",display:"flex",gap:"20px"}}>
                        <div><div style={{fontSize:"11px",color:"#9ca3af",fontWeight:"500"}}>Piyasa</div><div style={{fontSize:"13px",fontWeight:"600",color:"#e2e8f0"}}>🔒 {piyasa}</div></div>
                        <div><div style={{fontSize:"11px",color:"#9ca3af",fontWeight:"500"}}>Para Birimi</div><div style={{fontSize:"13px",fontWeight:"600",color:"#e2e8f0"}}>🔒 {paraBirimi}</div></div>
                    </div>
                )}
                {secilenModul && filtre && (
                    <div style={{marginBottom:"1.25rem"}}>
                        <div style={{fontSize:"12px",fontWeight:"600",color:"#93c5fd",marginBottom:"10px"}}>Tarama Kriterleri</div>
                        {renderFiltreler()}
                    </div>
                )}
                <div style={{display:"flex",gap:"8px",justifyContent:"flex-end"}}>
                    <button onClick={onIptal} style={{padding:"8px 18px",borderRadius:"8px",border:"1px solid #334155",background:"transparent",cursor:"pointer",fontSize:"13px",color:"#94a3b8"}}>İptal</button>
                    <button onClick={onBaslat} disabled={!secilenModul}
                        style={{padding:"8px 20px",borderRadius:"8px",border:"none",background:secilenModul?"linear-gradient(135deg,#1d4ed8,#3b82f6)":"#475569",color:"white",cursor:secilenModul?"pointer":"default",fontSize:"13px",fontWeight:"600"}}>
                        Taramayı Başlat →
                    </button>
                </div>
            </div>
        </div>
    );
}
